// ─────────────────────────────────────────────────────────────────────────────
// ExternalReferenceService
// ─────────────────────────────────────────────────────────────────────────────
// HTTP service for external reference CRUD and bulk operations.
// Endpoints: /ai/kb/external-references for all external reference operations.
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { APIResponse } from 'src/app/core/models/api-response';
import { IPageCount } from 'src/app/shared/models/page.model';
import { environment } from 'src/environments/environment';
import {
  ExternalReferenceFilters,
  ICreateExternalReferencePayload,
  IExternalReference,
  IUpdateExternalReferencePayload,
} from '../models/external-reference.models';
import { IImportPreviewResponse } from '../models/import.models';

@Injectable({
  providedIn: 'root',
})
export class ExternalReferenceService {
  constructor(private http: HttpClient) {}

  // ── List ───────────────────────────────────────────────────────────────────

  public listExternalReferences(
    page: IPageCount,
    key?: string,
    filters?: ExternalReferenceFilters,
    sortBy?: string,
    sortMode?: 'asc' | 'desc',
  ): Observable<APIResponse<IExternalReference>> {
    let params = new HttpParams().set('page', page.number).set('limit', page.size);

    if (key) {
      params = params.set('key', key);
    }

    if (filters) {
      if (filters.source) {
        params = params.set('source', filters.source);
      }
      if (filters.customer_id != null) {
        params = params.set('customer_id', filters.customer_id);
      }
      if (filters.product_id != null) {
        params = params.set('product_id', filters.product_id);
      }
      if (filters.create_date_from) {
        params = params.set('create_date_from', filters.create_date_from);
      }
      if (filters.create_date_to) {
        params = params.set('create_date_to', filters.create_date_to);
      }
    }

    if (sortBy) {
      params = params.set('sort_by', sortBy);
    }

    if (sortMode) {
      params = params.set('sort_mode', sortMode);
    }

    return this.http.get<APIResponse<IExternalReference>>(`${environment.BASE_URL_API}ai/kb/external-references`, { params });
  }

  // ── Create ─────────────────────────────────────────────────────────────────

  public createExternalReference(payload: ICreateExternalReferencePayload): Observable<APIResponse<IExternalReference>> {
    return this.http.post<APIResponse<IExternalReference>>(`${environment.BASE_URL_API}ai/kb/external-references`, payload);
  }

  // ── Update ─────────────────────────────────────────────────────────────────

  public updateExternalReference(id: number, payload: IUpdateExternalReferencePayload): Observable<APIResponse<IExternalReference>> {
    return this.http.post<APIResponse<IExternalReference>>(`${environment.BASE_URL_API}ai/kb/external-references/${id}`, payload);
  }

  // ── Delete ─────────────────────────────────────────────────────────────────

  public deleteExternalReference(id: number): Observable<APIResponse<IExternalReference>> {
    return this.http.delete<APIResponse<IExternalReference>>(`${environment.BASE_URL_API}ai/kb/external-references/${id}`);
  }

  // ── Bulk delete ────────────────────────────────────────────────────────────

  public bulkDeleteExternalReferences(ids: number[]): Observable<APIResponse<IExternalReference>> {
    return this.http.post<APIResponse<IExternalReference>>(`${environment.BASE_URL_API}ai/kb/external-references/bulk/delete`, { ids });
  }

  // ── Import ─────────────────────────────────────────────────────────────────

  /**
   * Upload a CSV/XLSX file for external reference import.
   * @param file FormData with field name 'ufile'
   * @param dryRun If true, validates without persisting (preview mode)
   */
  public importExternalReferences(file: FormData, dryRun = false): Observable<{ object: IImportPreviewResponse }> {
    const url = dryRun
      ? `${environment.BASE_URL_API}ai/kb/external-references/import?dry_run=1`
      : `${environment.BASE_URL_API}ai/kb/external-references/import`;
    return this.http.post<{ object: IImportPreviewResponse }>(url, file);
  }

  // ── Export ──────────────────────────────────────────────────────────────────

  public exportExternalReferences(
    format: 'csv' | 'xlsx' = 'xlsx',
    key?: string,
    filters?: ExternalReferenceFilters,
    sortBy?: string,
    sortMode?: 'asc' | 'desc',
  ): Observable<Blob> {
    let params = new HttpParams().set('format', format);

    if (key) params = params.set('key', key);
    if (sortBy) params = params.set('sort_by', sortBy);
    if (sortMode) params = params.set('sort_mode', sortMode);
    if (filters) {
      if (filters.source) params = params.set('source', filters.source);
      if (filters.customer_id != null) params = params.set('customer_id', filters.customer_id);
      if (filters.product_id != null) params = params.set('product_id', filters.product_id);
      if (filters.create_date_from) params = params.set('create_date_from', filters.create_date_from);
      if (filters.create_date_to) params = params.set('create_date_to', filters.create_date_to);
    }

    return this.http.get(`${environment.BASE_URL_API}ai/kb/external-references/export`, {
      params,
      responseType: 'blob',
    });
  }
}
